// ─── Core enums ───────────────────────────────────────────────────────────────

export type AgeGroup = 'tiny' | 'young' | 'middle'
export type Language = 'en' | 'sl' | 'it'
export type PuzzleType = 'sudoku' | 'crossword' | 'rebus' | 'maze' | 'math'
export type Difficulty = 'easy' | 'medium' | 'hard'

export const AGE_GROUP_LABELS: Record<AgeGroup, string> = {
  tiny:   'Tiny (4–6)',
  young:  'Young (7–9)',
  middle: 'Middle (10–12)',
}

export const LANGUAGE_LABELS: Record<Language, string> = {
  en: 'English',
  sl: 'Slovenian',
  it: 'Italian',
}

export const LANGUAGE_FLAGS: Record<Language, string> = {
  en: '🇬🇧',
  sl: '🇸🇮',
  it: '🇮🇹',
}

export const DIFFICULTY_LABELS: Record<Difficulty, string> = {
  easy:   'Easy',
  medium: 'Medium',
  hard:   'Hard',
}

// ─── Sudoku ───────────────────────────────────────────────────────────────────

export type SudokuSize = 4 | 6 | 9

export interface SudokuConfig {
  ageGroup:   AgeGroup
  size:       SudokuSize
  difficulty: Difficulty
}

/** A grid cell value: 0 = empty, 1–N = filled */
export type CellValue = number

export interface SudokuPuzzle {
  config:   SudokuConfig
  /** Flat array, row-major, length = size×size. 0 = blank. */
  given:    CellValue[]
  solution: CellValue[]
}

export const SUDOKU_SIZE_DEFAULTS: Record<AgeGroup, SudokuSize> = {
  tiny:   4,
  young:  6,
  middle: 9,
}

/** Pre-filled cell counts per (size, difficulty) */
export const SUDOKU_FILL_COUNTS: Record<SudokuSize, Record<Difficulty, number>> = {
  4: { easy: 12, medium: 10, hard: 8  },
  6: { easy: 24, medium: 18, hard: 14 },
  9: { easy: 36, medium: 28, hard: 20 },
}

// ─── Crossword ────────────────────────────────────────────────────────────────

export type CrosswordSize = 'small' | 'medium' | 'large'

export interface CrosswordConfig {
  ageGroup: AgeGroup
  language: Language
  size:     CrosswordSize
}

export interface CrosswordWord {
  word:      string
  clue:      string
  direction: 'across' | 'down'
  row:       number
  col:       number
  number:    number
}

export interface CrosswordPuzzle {
  config: CrosswordConfig
  words:  CrosswordWord[]
  /** Grid dimensions */
  rows:   number
  cols:   number
}

export const CROSSWORD_WORD_COUNTS: Record<CrosswordSize, { min: number; max: number }> = {
  small:  { min: 8,  max: 12 },
  medium: { min: 13, max: 20 },
  large:  { min: 21, max: 30 },
}

// ─── Rebus ────────────────────────────────────────────────────────────────────

export type RebusTileType = 'image' | 'text' | 'operator'
export type RebusCount = 1 | 4 | 8

export interface RebusTile {
  type:     RebusTileType
  /** Image URL, syllable/letter string, or operator symbol (+, –) */
  value:    string
  /** Alt text or pronunciation hint for image tiles */
  alt?:     string
}

export interface RebusEntry {
  id:          string
  targetWord:  string
  tiles:       RebusTile[]
  language:    Language
  ageGroup:    AgeGroup
  avgRating:   number
}

export interface RebusConfig {
  ageGroup: AgeGroup
  language: Language
  count:    RebusCount
}

// ─── Maze ─────────────────────────────────────────────────────────────────────

export type MazeSize = 'small' | 'medium' | 'large'

export interface MazeConfig {
  ageGroup: AgeGroup
  size:     MazeSize
}

/** Each cell tracks which walls are open. true = wall present (passable = false). */
export interface MazeCell {
  top:    boolean
  right:  boolean
  bottom: boolean
  left:   boolean
}

export interface MazePuzzle {
  config:   MazeConfig
  rows:     number
  cols:     number
  cells:    MazeCell[][]
  /** Entry: always top row */
  entryCol:  number
  /** Exit: always bottom row */
  exitCol:   number
  /** Solution: array of [row, col] pairs */
  solution: [number, number][]
}

export const MAZE_DIMENSIONS: Record<MazeSize, { rows: number; cols: number }> = {
  small:  { rows: 10, cols: 10 },
  medium: { rows: 15, cols: 15 },
  large:  { rows: 20, cols: 20 },
}

export const MAZE_SIZE_DEFAULTS: Record<AgeGroup, MazeSize> = {
  tiny:   'small',
  young:  'medium',
  middle: 'large',
}

// ─── Math ─────────────────────────────────────────────────────────────────────

export type MathOperation = 'addition' | 'subtraction' | 'multiplication' | 'division'
export type MathProblemCount = 10 | 20 | 30 | 50
export type MathLayout = 'list' | 'grid'
export type BlankPosition = 'result' | 'operand1' | 'operand2'

export interface MathConfig {
  ageGroup:     AgeGroup
  operations:   MathOperation[]
  resultMin:    number
  resultMax:    number
  problemCount: MathProblemCount
  layout:       MathLayout
}

export interface MathProblem {
  operand1:      number
  operand2:      number
  operation:     MathOperation
  result:        number
  blankPosition: BlankPosition
}

export const MATH_OPERATION_SYMBOLS: Record<MathOperation, string> = {
  addition:       '+',
  subtraction:    '−',
  multiplication: '×',
  division:       '÷',
}

export const MATH_DEFAULTS: Record<AgeGroup, Partial<MathConfig>> = {
  tiny:   { operations: ['addition'],                                    resultMin: 1,  resultMax: 10  },
  young:  { operations: ['addition', 'subtraction'],                     resultMin: 1,  resultMax: 20  },
  middle: { operations: ['addition', 'subtraction', 'multiplication', 'division'], resultMin: 1, resultMax: 100 },
}

// ─── API types (shared with worker) ──────────────────────────────────────────

export interface ApiWord {
  id:        string
  word:      string
  clues:     string[]
  language:  Language
  ageGroup:  AgeGroup
  avgRating: number
  source:    'seeded' | 'ai' | 'user'
}

export interface ApiError {
  error: string
  code:  number
}
